/** One monetary pool shared by both Bridges; not extra CLI account capacity. */
export interface ApiReserve {
  pool_id: string;
  currency: 'USD';
  state: 'available' | 'exhausted' | 'unavailable';
  shared_across_bridges: true;
  granted_usd: number;
  remaining_usd: number;
  available_usd: number;
  reserved_usd: number;
  account_count: number;
  eligible_accounts: number;
  stale_accounts: number;
  measured_at: string | null;
  checked_at: string;
  next_expiry_at: string | null;
  automatic_paid_fallback: false;
  supports_tools: false;
  supports_claude_code: false;
  supported_operations: ['text', 'vision'];
  requires_stateless_request: true;
}

export interface ApiReserveView {
  reserve: ApiReserve | null;
  bridges: Record<'dev' | 'prod', 'active' | 'inactive' | 'unavailable'>;
  reason: string | null;
}

/** Whitelist the wire contract. Never forward arbitrary internal JSON to UI. */
export function parseApiReserve(raw: unknown, now = Date.now()): ApiReserve | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  if (typeof d.pool_id !== 'string' || !d.pool_id || d.currency !== 'USD'
      || d.shared_across_bridges !== true || d.automatic_paid_fallback !== false
      || d.supports_tools !== false || d.supports_claude_code !== false
      || d.requires_stateless_request !== true
      || !['available', 'exhausted', 'unavailable'].includes(String(d.state))) return null;
  const amounts = ['granted_usd', 'remaining_usd', 'available_usd', 'reserved_usd',
    'account_count', 'eligible_accounts', 'stale_accounts'] as const;
  if (amounts.some(k => typeof d[k] !== 'number' || !Number.isFinite(d[k]) || (d[k] as number) < 0)) return null;
  const checked = Date.parse(String(d.checked_at));
  if (!Number.isFinite(checked) || now - checked > 90_000 || checked > now + 60_000) return null;
  if (!Array.isArray(d.supported_operations) || !d.supported_operations.includes('text')
      || !d.supported_operations.includes('vision')) return null;
  const measured = d.measured_at === null ? null : Date.parse(String(d.measured_at));
  if (d.state === 'available' && (measured === null || !Number.isFinite(measured)
      || now - measured > 3_600_000 || measured > now + 60_000 || !(Number(d.available_usd) > 0))) return null;
  if (d.next_expiry_at !== null && !Number.isFinite(Date.parse(String(d.next_expiry_at)))) return null;
  return {
    pool_id: d.pool_id, currency: 'USD', state: d.state as ApiReserve['state'],
    shared_across_bridges: true, granted_usd: Number(d.granted_usd),
    remaining_usd: Number(d.remaining_usd), available_usd: Number(d.available_usd),
    reserved_usd: Number(d.reserved_usd), account_count: Number(d.account_count),
    eligible_accounts: Number(d.eligible_accounts), stale_accounts: Number(d.stale_accounts),
    measured_at: d.measured_at === null ? null : String(d.measured_at),
    checked_at: String(d.checked_at), next_expiry_at: d.next_expiry_at === null ? null : String(d.next_expiry_at),
    automatic_paid_fallback: false, supports_tools: false, supports_claude_code: false,
    supported_operations: ['text', 'vision'], requires_stateless_request: true,
  };
}

export function combineApiReserves(dev: unknown, prod: unknown, now = Date.now()): ApiReserveView {
  const snapshots = { dev, prod };
  const bridges: ApiReserveView['bridges'] = { dev: 'unavailable', prod: 'unavailable' };
  const valid: ApiReserve[] = [];
  for (const name of ['dev', 'prod'] as const) {
    const raw = snapshots[name] as Record<string, unknown> | null;
    const parsed = parseApiReserve(raw, now);
    if (parsed && raw?.active_on_this_bridge === true) {
      bridges[name] = 'active'; valid.push(parsed);
    } else if (raw?.state === 'not_configured') bridges[name] = 'inactive';
  }
  if (new Set(valid.map(r => r.pool_id)).size > 1) return { reserve: null, bridges, reason: 'pool_mismatch' };
  // Never add the same shared balance twice. Take the newest observation.
  valid.sort((a, b) => Date.parse(b.checked_at) - Date.parse(a.checked_at));
  return { reserve: valid[0] ?? null, bridges, reason: valid.length ? null : 'reserve_unavailable' };
}
